/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import { IPlayResult, IPlayState } from '../Server/Object/PlayResult';
import SplashScreenComponent from '../StartUp/SplashScreenComponent';
import StateDependencyComponent from '../StateManager/StateDependencyComponent';
import StateEvent from '../StateManager/StateEvent';
import { StateID } from '../StateManager/StateID';
import StateReference from '../StateManager/StateReference';
import InjectableComponent from '../Utility/Component/ComponentReference/InjectableComponent';
import DtModObservable from './DtModObservable';
import GameSetup from './GameSetup';
import ClientEvents from './Integrations/ClientEvents';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/**
 * RecoveryManager is responsible for restoring the game state when the previous play
 * was interrupted by a client closure mid-game.
 */
@cc._decorator.ccclass
export default class RecoveryManager extends StateDependencyComponent
{
    /** The state on which to check if recovery is needed. */
    @property({ type: StateReference, visible: true })
    private _checkRecoveryOnState: StateReference = new StateReference(StateID.CheckRecovery, true);

    /** Visible component to be overlayed onto the UI during recovery. */
    @property({ type: cc.Node, visible: true })
    private _cover: cc.Node = null;

    /** Game execution speed modifier while recovery is taking place. */
    @property({ type: cc.Integer, visible: true })
    private _recoveryDTMod: number = 3;

    protected startingDTMod: number = 1;

    private _isRecovering: boolean = false;

    /** @hidden */
    @property(InjectableComponent)
    protected gameSetupRef: InjectableComponent<GameSetup> = new InjectableComponent<GameSetup>();

    /** @hidden */
    protected splashScreenRef: InjectableComponent<SplashScreenComponent>
        = new InjectableComponent<SplashScreenComponent>();

    /** @hidden */
    private _dtModRef: InjectableComponent<DtModObservable> = new InjectableComponent();

    /** @hidden */
    private _clientEventsRef: InjectableComponent<ClientEvents> = new InjectableComponent();

    /** @hidden */
    private _recoveryCurrentPlayID: number = 0;
    /** Mutator for recovery's current play ID. */
    public set RecoveryCurrentPlayID(playID: number) { this._recoveryCurrentPlayID = playID; }
    /** Accessor for recovery's current play ID. */
    public get RecoveryCurrentPlayID(): number { return this._recoveryCurrentPlayID; }
    /** Accessor for the target play ID we need to recover up to. */
    public get RecoveryTargetPlayID(): number
    {
        let gameSetup: GameSetup = this.gameSetupRef.Value;
        if (gameSetup == null) { return 1; }

        let currentState: IPlayState = gameSetup.LastPlayState;
        if (currentState == null) { return 1; }

        let lastPlayResult: IPlayResult = currentState.playResult;
        let playID: number = lastPlayResult.playId;
        return playID;
    }

    /**
     * Determines if recover has reached the target play ID.
     * @returns If current play ID has reached the target play ID.
     */
    public get AtLatestPlay(): boolean
    {
        return (this.RecoveryCurrentPlayID >= this.RecoveryTargetPlayID);
    }

    /**
     * Accessor for whether or not the latest play has been shown.
     */
    public get ShownLatestPlay(): boolean
    {
        let pastTargetPlay: boolean = this.RecoveryCurrentPlayID > this.RecoveryTargetPlayID;

        return this.gameSetupRef.Value.LastPlayState.resultDisplayed || pastTargetPlay;
    }

    /**
     * Accessor for whether or not we are in the recovery process.
     */
    public get Recovering(): boolean
    {
        if (!this.needsRecovery()) { return false; }
        if (!this.AtLatestPlay) { return true; }
        if (this.ShownLatestPlay) { return false; }
        return true;
    }

    public get Progress(): number
    {
        return (this.RecoveryCurrentPlayID / (this.RecoveryTargetPlayID + 1));
    }

    /** @hidden */
    protected setContexts(): void
    {
        this.gameSetupRef.SetContext(GameSetup);
        this.splashScreenRef.SetContext(SplashScreenComponent);
        this._dtModRef.SetContext(DtModObservable);
        this._clientEventsRef.SetContext(ClientEvents);
    }

    /** @hidden */
    protected start(): void
    {
        super.start([this._checkRecoveryOnState]);

        this.startingDTMod = this._dtModRef.Value.Value;
    }

    /** @hidden */
    protected onStateChangeHandler = (event: StateEvent): void =>
    {
        if (event.State === this._checkRecoveryOnState.Value)
        {
            this.checkRecovery();
        }
    }

    /**
     * Determines whether or not recovery needs to take place.
     * @returns Whether or not recovery needs to take place.
     * @hidden
     */
    private needsRecovery(): boolean
    {
        return this.gameSetupRef.WithValue(gameSetup =>
            {
                return gameSetup.LastPlayState
                    && gameSetup.LastPlayState.playResult.GameOver === 0;
            }, false);
    }

    /**
     * Called when checkRecoveryOnState is reached.
     * Begins the recovery process if necessary.
     */
    private checkRecovery(): void
    {
        if (this.needsRecovery())
        {
            this._recoveryCurrentPlayID = 0;
            this.completeStateOptionalNext(this._checkRecoveryOnState.Value);
            if (this.AtLatestPlay === false)
            {
                this.StartRecoveryMode();
            }
            else if (this.splashScreenRef.HasValue())
            {
                this.splashScreenRef.Value.Hide(true);
            }
        }
        else
        {
            this.completeState(this._checkRecoveryOnState.Value);
        }
    }

    /**
     * Speeds up the games execution speed and enables the UI cover.
     */
    public StartRecoveryMode(): void
    {
        this._isRecovering = true;
        this._dtModRef.Value.SetValue(this._recoveryDTMod);
        if (this._cover)
        {
            this._cover.active = true;
        }
    }

    /**
     * Returns the game to normal execution speed and removes the UI cover.
     */
    public EndRecoveryMode(): void
    {
        if (!this._isRecovering) { return; }
        if (this._cover) { this._cover.active = false; }

        this._dtModRef.Value.SetValue(this.startingDTMod);

        this.onRecoveryCompleteHandlers.forEach(handler => handler());
        this.onRecoveryCompleteHandlers.length = 0;

        this.splashScreenRef.WithValue(splashScreen => splashScreen.Hide(true));
        this._clientEventsRef.WithValue(clientEvents => clientEvents.GameLoaded());
        this._isRecovering = false;
    }

    protected onRecoveryCompleteHandlers: (() => any)[] = [];

    public OnRecoveryComplete(handler: () => any): void
    {
        this.onRecoveryCompleteHandlers.push(handler);
    }
}
